import React from 'react'
import { Helmet } from 'react-helmet';
import AppLayoutTitle from './appLayoutTitle';
import Swal from 'sweetalert2';
import { apiGet, apiPost } from '../../helper/helperApi';
import { handleException } from '../../helper/helperError';
import ComTable from './../components/comTable';
import ComRuleEditor from './../components/comRuleEditor';
import { createRoot } from 'react-dom/client';


const AppEmailRules = () => {
    const [dataset, setDataset] = React.useState(undefined);
    const [activeDomains, setActiveDomains] = React.useState(undefined);
    const firstTimeUse = (dataset !== undefined && Array.isArray(dataset) && dataset.length === 0)

    const fetchData = async () => {
        try {
            apiGet("/rules")
                .then((res) => { setDataset(res?.data?.sort((a, b) => a.priority - b.priority) || []); })
                .catch((ex) => handleException(ex, "Failed to fetch rules data!"));
            apiGet("/domains/activedomains")
                .then((res) => { setActiveDomains(res.data || []); })
                .catch((ex) => handleException(ex, "Failed to fetch active domains data!"));
        } catch (ex) {
            handleException(ex, "Failed to fetch rules data!");
        }
    }

    React.useEffect(() => {
        fetchData();
    }, []);

    const handleAddRule = async () => {
        // 1- Check active domains
        if (!activeDomains || activeDomains.length === 0) {
            return Swal.fire({
                title: 'No Active Domains',
                text: 'Please add an active domain before creating a rule.',
                icon: 'warning',
                customClass: {
                    confirmButton: 'btn-gold text-dark',
                    cancelButton: 'btn-light'
                },
                confirmButtonText: 'OK',
                showCancelButton: false,
            });
        }

        // 2- Show the form
        let formData = null;
        Swal.fire({
            title: 'Add Rule',
            html: '<div id="swal-react-root"></div>',
            width: "1200px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Add Rule',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
            didOpen: () => {
                const swalRoot = document.getElementById('swal-react-root');
                if (swalRoot) {
                    const root = createRoot(swalRoot);
                    root.render(
                        <ComRuleEditor
                            activeDomains={activeDomains}
                            onDataChange={(data) => { formData = data; }}
                        />
                    );
                }
            },
            preConfirm: () => {
                if (!formData
                    || !formData.ruleName
                    || !formData.domainFrom
                    || !formData.subjectStyle
                    || !formData.translationLanguage
                    || !formData.translationEngine
                    || !formData.bodyStyle
                    || !formData.bodyColor
                    || !formData.bodyFont) {
                    Swal.showValidationMessage(`Please fill all fields`);
                }
                if (formData.translationEngine === "google-translate-v3"
                    || formData.translationEngine === "gpt-4.1"
                    || formData.translationEngine === "o4-mini"
                ) {
                    Swal.showValidationMessage(`The selected translation engine is not supported at the moment!`);
                }
                return formData;
            },
        }).then((result) => {
            if (result.isConfirmed) {
                const newData = {
                    ...result.value,
                    priority: dataset.length + 1,
                }
                apiPost("/rules/new", newData)
                    .then(() => {
                        Swal.fire({
                            title: 'Added!',
                            text: 'Your rule has been added.',
                            icon: 'success',
                            timer: 1500,
                        })
                        fetchData();
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to add rule");
                        fetchData();
                    })
            }
        });
    }

    const handleDelete = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid rule data");
        Swal.fire({
            title: 'Delete Rule',
            text: `Are you sure you want to delete this rule? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete Rule',
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
        }).then((result) => {
            if (result.isConfirmed) {
                apiPost(`/rules/${row.id}/delete`)
                    .then(() => {
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Your rule has been deleted.',
                            icon: 'success',
                            timer: 1500,
                        })
                        fetchData();
                    })
                    .catch((ex) => {
                        handleException(ex, "Failed to delete rule");
                        fetchData();
                    })
            }
        });
    }

    const handleEdit = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid rule data");

        try {
            const res = await apiGet(`/rules/${row.id}`);
            const ruleData = res.data;
            let formData = { ...ruleData };
            Swal.fire({
                title: 'Edit Rule',
                html: '<div id="swal-react-root"></div>',
                width: "1200px",
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Save Changes',
                customClass: {
                    confirmButton: 'btn-gold text-dark',
                    cancelButton: 'btn-light'
                },
                didOpen: () => {
                    const swalRoot = document.getElementById('swal-react-root');
                    if (swalRoot) {
                        const root = createRoot(swalRoot);
                        root.render(
                            <ComRuleEditor
                                initialData={ruleData}
                                activeDomains={activeDomains}
                                onDataChange={(data) => { formData = data; }}
                            />
                        );
                    }
                },
                preConfirm: () => {
                    if (!formData
                        || !formData.ruleName
                        || !formData.domainFrom
                        || !formData.subjectStyle
                        || !formData.translationLanguage
                        || !formData.translationEngine
                        || !formData.bodyStyle
                        || !formData.bodyColor
                        || !formData.bodyFont) {
                        Swal.showValidationMessage(`Please fill all fields`);
                    }
                    if (formData.translationEngine === "google-translate-v3"
                        || formData.translationEngine === "gpt-4.1"
                        || formData.translationEngine === "o4-mini"
                    ) {
                        Swal.showValidationMessage(`The selected translation engine is not supported at the moment!`);
                    }
                    return formData;
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    apiPost(`/rules/${row.id}/update`, {
                        ...result.value,
                        priority: ruleData.priority,
                    })
                        .then(() => {
                            Swal.fire({
                                title: 'Updated!',
                                text: 'Your rule has been updated.',
                                icon: 'success',
                                timer: 1500,
                            })
                            fetchData();
                        })
                        .catch((ex) => {
                            handleException(ex, "Failed to update rule");
                            fetchData();
                        })
                }
            });
        }
        catch (ex) {
            handleException(ex, "Failed to edit rule");
        }
    }

    const handleMoveUp = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid rule data");
        const currentPriority = row.priority;
        const currentId = row.id;
        const prevRow = dataset.find((item) => item.priority === currentPriority - 1);
        if (!prevRow) return handleException(null, "Invalid rule data");
        const prevId = prevRow.id;
        apiPost(`/rules/swap/`, {
            id1: currentId,
            priority1: currentPriority,
            id2: prevId,
            priority2: currentPriority - 1,
        })
            .then(() => {
                Swal.fire({
                    title: 'Updated!',
                    text: 'Your rule has been updated.',
                    icon: 'success',
                    timer: 1500,
                })
                fetchData();
            })
            .catch((ex) => {
                handleException(ex, "Failed to update rule");
                fetchData();
            })
    }

    const handleMoveDown = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid rule data!");
        const currentPriority = row.priority;
        const currentId = row.id;
        const nextRow = dataset.find((item) => item.priority === currentPriority + 1);
        if (!nextRow) return handleException(null, "Invalid rule data!");
        const nextId = nextRow.id;
        apiPost(`/rules/swap/`, {
            id1: currentId,
            priority1: currentPriority,
            id2: nextId,
            priority2: currentPriority + 1,
        })
            .then(() => {
                Swal.fire({
                    title: 'Updated!',
                    text: 'Your rule has been updated.',
                    icon: 'success',
                    timer: 1500,
                })
                fetchData();
            })
            .catch((ex) => {
                handleException(ex, "Failed to update rule!");
                fetchData();
            })
    }

    return <>
        <Helmet>
            <title>Email Rules | Sparkling Email</title>
        </Helmet>
        <>
            <AppLayoutTitle title="Email Rules" buttons={
                <button className="btn btn-gold" onClick={handleAddRule}>Add New Rule</button>
            } />
            <div className="px-3 py-3">
                <div>
                    Define when and how incoming or outgoing emails should be translated.
                </div>
                <div>
                    Create rules based on sender, recipient, subject keywords, language headers
                    or other criteria, then choose the target language and execution order. Use
                    this page to add, edit or remove translation rules and control exactly which
                    messages Sparkling Email will process.
                </div>
                <br />
                {firstTimeUse === true
                    ? <div className="text-center my-5">
                        <button className="btn btn-lg btn-outline-secondary" onClick={handleAddRule}>
                            Add First Rule
                        </button>
                    </div>
                    : <ComTable
                        data={dataset}
                        isUndefinedIsLoading={true}
                        handleDelete={(row) => handleDelete(row)}
                        handleEdit={(row) => handleEdit(row)}
                        handleMoveUp={(row) => handleMoveUp(row)}
                        handleMoveDown={(row) => handleMoveDown(row)}
                    />
                }
            </div>
        </>
    </>
}

export default AppEmailRules;